import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { Box, Calculator, Clock, DollarSign, Edit2, Package, Plus, Ruler, Trash2, X } from "lucide-react";
import React, { useState, useMemo } from "react";
import {
  CreatePackingMetricDto,
  PackingMetric,
  PackingCalculationType,
  useCreatePackingMetric,
  useDeletePackingMetric,
  useGetPackingMetrics,
  useUpdatePackingMetric,
  useGetPackagingMaterials,
  useGetPackingMetricSupplies,
  useAddPackingMetricSupply,
  useRemovePackingMetricSupply,
  useGetMaterialBreakdown,
} from "~/services/service-metrics";
import { capitalize } from "~/utils";
import { PackingTypes } from "~/utils/constants";

interface PackingMetricFormData {
  packingType: string;
  ratePerCubicFoot: number;
  minimumCharge: number;
  maxLength?: number;
  maxWidth?: number;
  maxHeight?: number;
  isActive: boolean;
  estimatedMinutesPerItem?: number;
  materialRatePerSqFt?: number;
}

const CALCULATION_TYPE_OPTIONS = [
  { value: 'surface_area', label: 'Surface Area (sq ft)', description: '2(LW + LH + WH)' },
  { value: 'perimeter', label: 'Perimeter (linear ft)', description: '4(L + W + H)' },
  { value: 'volume', label: 'Volume (cu ft)', description: 'L × W × H' },
  { value: 'fixed', label: 'Fixed Quantity', description: 'Per item' },
];

export const PackingMetricsTab: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMetric, setEditingMetric] = useState<PackingMetric | null>(null);
  const [form] = Form.useForm<PackingMetricFormData>();

  // Preview dimensions for material breakdown (default to max dims or these)
  const [previewLength, setPreviewLength] = useState<number>(48);
  const [previewWidth, setPreviewWidth] = useState<number>(36);
  const [previewHeight, setPreviewHeight] = useState<number>(4);

  // Material add form state
  const [selectedSupplyId, setSelectedSupplyId] = useState<string>("");
  const [selectedCalcType, setSelectedCalcType] = useState<PackingCalculationType>("surface_area");
  const [quantityMultiplier, setQuantityMultiplier] = useState<number>(1);
  const [wasteFactor, setWasteFactor] = useState<number>(1);

  // Labor rate for calculations
  const [hourlyRate, setHourlyRate] = useState<number>(90);

  // Watch form values for reactive updates
  const watchedEstimatedMinutes = Form.useWatch("estimatedMinutesPerItem", form) || 0;
  const watchedMaxLength = Form.useWatch("maxLength", form);
  const watchedMaxWidth = Form.useWatch("maxWidth", form);
  const watchedMaxHeight = Form.useWatch("maxHeight", form);

  const { data: packingMetrics, isLoading } = useGetPackingMetrics();
  const { data: packagingMaterials } = useGetPackagingMaterials();

  // Get linked supplies and material breakdown when editing
  const { data: linkedSupplies, isLoading: isLoadingSupplies } = useGetPackingMetricSupplies(
    editingMetric?.id || ""
  );
  const { data: materialBreakdown, isLoading: isLoadingBreakdown } = useGetMaterialBreakdown(
    editingMetric?.id || "",
    watchedMaxLength || previewLength,
    watchedMaxWidth || previewWidth,
    watchedMaxHeight || previewHeight,
    !!editingMetric?.id
  );

  const createMutation = useCreatePackingMetric();
  const updateMutation = useUpdatePackingMetric();
  const deleteMutation = useDeletePackingMetric();
  const addSupplyMutation = useAddPackingMetricSupply();
  const removeSupplyMutation = useRemovePackingMetricSupply();

  // Filter out already linked supplies
  const availableSupplies = useMemo(() => {
    return packagingMaterials?.filter(
      (supply) => !linkedSupplies?.some((ls) => ls.supplyId === supply.id)
    ) || [];
  }, [packagingMaterials, linkedSupplies]);

  // Calculate suggested charge (materials + labor)
  const suggestedCharge = useMemo(() => {
    if (!materialBreakdown) return { materials: 0, labor: 0, total: 0 };
    const materials = materialBreakdown.totalMaterialCost || 0;
    const labor = (watchedEstimatedMinutes / 60) * hourlyRate;
    return {
      materials,
      labor,
      total: materials + labor,
    };
  }, [materialBreakdown, watchedEstimatedMinutes, hourlyRate]);

  const handleCreate = () => {
    setEditingMetric(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      ratePerCubicFoot: 0,
      minimumCharge: 0,
      estimatedMinutesPerItem: 0,
      materialRatePerSqFt: 0,
    });
    setIsModalVisible(true);
  };

  const handleEdit = (record: PackingMetric) => {
    setEditingMetric(record);
    form.setFieldsValue({
      packingType: record.packingType,
      ratePerCubicFoot: Number(record.baseRate),
      minimumCharge: Number(record.minimumCharge),
      maxLength: Number(record.maxLength ?? 0),
      maxWidth: Number(record.maxWidth ?? 0),
      maxHeight: Number(record.maxHeight ?? 0),
      isActive: record.isActive,
      estimatedMinutesPerItem: Number(record.estimatedMinutesPerItem ?? 0),
      materialRatePerSqFt: Number((record as any).materialRatePerSqFt ?? 0),
    });
    // Set preview dimensions from max dimensions
    if (record.maxLength) setPreviewLength(Number(record.maxLength));
    if (record.maxWidth) setPreviewWidth(Number(record.maxWidth));
    if (record.maxHeight) setPreviewHeight(Number(record.maxHeight));
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  const handleSubmit = async (values: PackingMetricFormData) => {
    try {
      const data: CreatePackingMetricDto = {
        packingType: values.packingType,
        baseRate: values.ratePerCubicFoot,
        minimumCharge: values.minimumCharge,
        maxLength: values.maxLength,
        maxWidth: values.maxWidth,
        maxHeight: values.maxHeight,
        isActive: values.isActive,
        estimatedMinutesPerItem: values.estimatedMinutesPerItem,
        materialRatePerSqFt: values.materialRatePerSqFt,
      };

      if (editingMetric) {
        await updateMutation.mutateAsync({ id: editingMetric.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingMetric(null);
    } catch (error) {
      console.error("Failed to save packing metric:", error);
    }
  };

  const handleAddMaterial = async () => {
    if (!editingMetric?.id || !selectedSupplyId) return;

    await addSupplyMutation.mutateAsync({
      packingMetricId: editingMetric.id,
      data: {
        supplyId: selectedSupplyId,
        calculationType: selectedCalcType,
        quantityMultiplier,
        wasteFactor,
      },
    });

    // Reset form
    setSelectedSupplyId("");
    setSelectedCalcType("surface_area");
    setQuantityMultiplier(1);
    setWasteFactor(1);
  };

  const handleRemoveMaterial = async (linkId: string) => {
    if (!editingMetric?.id) return;
    await removeSupplyMutation.mutateAsync({
      id: linkId,
      packingMetricId: editingMetric.id,
    });
  };

  const handleUseCalculatedRate = () => {
    if (materialBreakdown?.dimensions?.surfaceAreaSqFt && suggestedCharge.total > 0) {
      const rate = suggestedCharge.total / materialBreakdown.dimensions.surfaceAreaSqFt;
      form.setFieldsValue({ ratePerCubicFoot: Math.round(rate * 100) / 100 });
    }
  };

  const handleUseCalculatedMinimum = () => {
    form.setFieldsValue({ minimumCharge: Math.round(suggestedCharge.total * 100) / 100 });
  };

  // Calculate material-only rate (no labor) for use in estimates
  const handleUseCalculatedMaterialRate = () => {
    if (materialBreakdown?.dimensions?.surfaceAreaSqFt && suggestedCharge.materials > 0) {
      const rate = suggestedCharge.materials / materialBreakdown.dimensions.surfaceAreaSqFt;
      form.setFieldsValue({ materialRatePerSqFt: Math.round(rate * 10000) / 10000 });
    }
  };

  const columns = [
    {
      title: "Packing Type",
      dataIndex: "packingType",
      key: "packingType",
      render: (type: string) => (
        <span className="flex items-center">
          <Package size={16} className="mr-2 text-purple-600" />
          <strong>{type}</strong>
        </span>
      ),
    },
    {
      title: "Billable Rate",
      dataIndex: "baseRate",
      key: "baseRate",
      render: (rate: number | string) => (
        <span className="flex items-center">
          <DollarSign size={14} className="mr-1 text-green-600" />
          <span className="font-semibold">{Number(rate)?.toFixed(2) || "0.00"}</span>
          <span className="text-gray-500 ml-1">/ft²</span>
        </span>
      ),
      sorter: (a: PackingMetric, b: PackingMetric) =>
        (Number(a.baseRate) || 0) - (Number(b.baseRate) || 0),
    },
    {
      title: "Material Rate",
      dataIndex: "materialRatePerSqFt",
      key: "materialRatePerSqFt",
      render: (rate: number | string) => (
        <span className="flex items-center">
          <Package size={14} className="mr-1 text-purple-600" />
          <span className="font-semibold">{Number(rate)?.toFixed(4) || "0.0000"}</span>
          <span className="text-gray-500 ml-1">/ft²</span>
        </span>
      ),
    },
    {
      title: "Est. Cost at Max",
      dataIndex: "minimumCharge",
      key: "minimumCharge",
      render: (charge: number | string) => {
        const numericCharge = typeof charge === "string" ? parseFloat(charge) : charge;
        return (
          <span className="flex items-center">
            <DollarSign size={14} className="mr-1" />
            {(numericCharge || 0).toFixed(2)}
          </span>
        );
      },
    },
    {
      title: "Est. Time",
      dataIndex: "estimatedMinutesPerItem",
      key: "estimatedMinutesPerItem",
      render: (minutes: number | string | null) => (
        <span className="flex items-center">
          <Clock size={14} className="mr-1 text-orange-500" />
          {Number(minutes) || 0} min
        </span>
      ),
    },
    {
      title: "Max Dimensions",
      key: "dimensions",
      render: (_: any, record: PackingMetric) => {
        if (!record.maxLength && !record.maxWidth && !record.maxHeight) {
          return <span className="text-gray-400">No limit</span>;
        }
        return (
          <span className="flex items-center text-sm">
            <Ruler size={14} className="mr-1" />
            {record.maxLength}×{record.maxWidth}×{record.maxHeight}"
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      onFilter: (value: any, record: PackingMetric) => record.isActive === value,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: PackingMetric) => (
        <Space>
          <Button
            type="text"
            icon={<Edit2 size={16} />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Are you sure to delete this packing metric?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<Trash2 size={16} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Calculate statistics
  const activeMetrics = packingMetrics?.filter((metric) => metric.isActive) || [];
  const avgRate = activeMetrics.length > 0
    ? activeMetrics.reduce((sum, metric) => sum + (Number(metric.baseRate) || 0), 0) / activeMetrics.length
    : 0;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Packing Metrics</h3>
          <p className="text-gray-500">
            Manage pricing, materials, and time estimates for different packing types.
          </p>
        </div>
        <Button type="primary" icon={<Plus size={16} />} onClick={handleCreate}>
          Add Packing Metric
        </Button>
      </div>

      {/* Statistics Cards */}
      {activeMetrics.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="text-sm text-purple-600 mb-1">Active Types</div>
            <div className="text-2xl font-bold text-purple-900">{activeMetrics.length}</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-sm text-green-600 mb-1">Average Rate</div>
            <div className="text-2xl font-bold text-green-900">${avgRate.toFixed(2)}/ft²</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="text-sm text-orange-600 mb-1">Total Materials Configured</div>
            <div className="text-2xl font-bold text-orange-900">{packagingMaterials?.length || 0}</div>
          </div>
        </div>
      )}

      <Alert
        message="Packing Metrics with Material Linking"
        description="Configure packing types with linked materials. The system calculates surface area from dimensions and computes material costs based on billable rates per sq ft."
        type="info"
        showIcon
        className="mb-4"
      />

      <Table
        columns={columns}
        dataSource={[...(packingMetrics || [])].sort((a, b) => {
          // Sort by packing type first, then by cost at max
          const typeCompare = a.packingType.localeCompare(b.packingType);
          if (typeCompare !== 0) return typeCompare;
          return (Number(a.minimumCharge) || 0) - (Number(b.minimumCharge) || 0);
        })}
        rowKey="id"
        loading={isLoading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} packing types`,
        }}
      />

      {/* Edit Modal */}
      <Modal
        title={
          <div className="flex items-center">
            <Package size={20} className="mr-2 text-purple-600" />
            {editingMetric ? `Edit: ${editingMetric.packingType}` : "Create Packing Metric"}
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingMetric(null);
        }}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={24}>
            {/* Left Column - Form Fields */}
            <Col span={12}>
              <Form.Item
                name="packingType"
                label="Packing Type"
                rules={[{ required: true, message: "Please select packing type" }]}
              >
                <Select
                  options={Object.values(PackingTypes).map((type) => ({
                    label: capitalize(type),
                    value: type,
                  }))}
                  placeholder="Select packing type"
                  showSearch
                  disabled={!!editingMetric}
                />
              </Form.Item>

              <div className="border rounded-lg p-3 mb-4 bg-gray-50">
                <h4 className="text-sm font-medium mb-2">Max Dimensions <span className="text-red-500">*</span></h4>
                <Row gutter={8}>
                  <Col span={8}>
                    <Form.Item name="maxLength" label="L" className="mb-0" rules={[{ required: true, message: "Required" }]}>
                      <InputNumber min={0} className="w-full" addonAfter="in" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="maxWidth" label="W" className="mb-0" rules={[{ required: true, message: "Required" }]}>
                      <InputNumber min={0} className="w-full" addonAfter="in" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="maxHeight" label="H" className="mb-0" rules={[{ required: true, message: "Required" }]}>
                      <InputNumber min={0} className="w-full" addonAfter="in" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <Form.Item name="estimatedMinutesPerItem" label="Estimated Minutes per Item">
                <InputNumber min={0} step={5} precision={0} className="w-full" addonAfter="minutes" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="ratePerCubicFoot"
                    label={
                      <span>
                        Billable Rate/Sq Ft{" "}
                        {suggestedCharge.total > 0 && (
                          <Tooltip title="Use calculated rate from materials + labor">
                            <Button type="link" size="small" onClick={handleUseCalculatedRate} className="p-0 h-auto">
                              (use calculated)
                            </Button>
                          </Tooltip>
                        )}
                      </span>
                    }
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <InputNumber prefix="$" min={0} step={0.01} precision={2} className="w-full" addonAfter="/ft²" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="minimumCharge"
                    label={
                      <span>
                        Est. Cost at Max{" "}
                        {suggestedCharge.total > 0 && (
                          <Tooltip title="Use calculated total from materials + labor">
                            <Button type="link" size="small" onClick={handleUseCalculatedMinimum} className="p-0 h-auto">
                              (use calculated)
                            </Button>
                          </Tooltip>
                        )}
                      </span>
                    }
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <InputNumber prefix="$" min={0} step={0.01} precision={2} className="w-full" />
                  </Form.Item>
                </Col>
              </Row>

              {/* Material Rate - for estimates (no labor) */}
              <Form.Item
                name="materialRatePerSqFt"
                label={
                  <span className="flex items-center gap-2">
                    <Package size={14} className="text-purple-600" />
                    Material Rate/Sq Ft (for estimates){" "}
                    {suggestedCharge.materials > 0 && (
                      <Tooltip title="Use calculated rate from materials only (no labor)">
                        <Button type="link" size="small" onClick={handleUseCalculatedMaterialRate} className="p-0 h-auto">
                          (use calculated)
                        </Button>
                      </Tooltip>
                    )}
                  </span>
                }
              >
                <InputNumber prefix="$" min={0} step={0.0001} precision={4} className="w-full" addonAfter="/ft²" />
              </Form.Item>

              <Form.Item name="isActive" label="Status" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>

            {/* Right Column - Materials & Calculations (only when editing) */}
            <Col span={12}>
              {editingMetric ? (
                <div className="space-y-4">
                  {/* Linked Materials */}
                  <div className="border rounded-lg p-3 bg-purple-50">
                    <h4 className="text-sm font-medium mb-2 flex items-center">
                      <Box size={16} className="mr-2 text-purple-600" />
                      Linked Materials ({linkedSupplies?.length || 0})
                    </h4>

                    {/* Add Material Form */}
                    <div className="space-y-2 mb-3">
                      <Select
                        placeholder="Add material..."
                        className="w-full"
                        size="small"
                        value={selectedSupplyId || undefined}
                        onChange={setSelectedSupplyId}
                        options={availableSupplies.map((s) => ({
                          value: s.id,
                          label: `${s.name}${s.unit ? ` (${s.unit})` : ""}`,
                        }))}
                        showSearch
                        optionFilterProp="label"
                      />
                      {selectedSupplyId && (
                        <>
                          <Row gutter={8}>
                            <Col span={12}>
                              <Select
                                className="w-full"
                                size="small"
                                value={selectedCalcType}
                                onChange={setSelectedCalcType}
                                options={CALCULATION_TYPE_OPTIONS}
                              />
                            </Col>
                            <Col span={6}>
                              <InputNumber
                                className="w-full"
                                size="small"
                                value={quantityMultiplier}
                                onChange={(v) => setQuantityMultiplier(v || 1)}
                                min={0.01}
                                step={0.1}
                                addonBefore="×"
                              />
                            </Col>
                            <Col span={6}>
                              <InputNumber
                                className="w-full"
                                size="small"
                                value={wasteFactor}
                                onChange={(v) => setWasteFactor(v || 1)}
                                min={1}
                                max={2}
                                step={0.05}
                                addonAfter="%"
                                formatter={(v) => `${((Number(v) - 1) * 100).toFixed(0)}`}
                                parser={(v) => 1 + (Number(v) || 0) / 100}
                              />
                            </Col>
                          </Row>
                          <Button
                            type="primary"
                            size="small"
                            icon={<Plus size={14} />}
                            onClick={handleAddMaterial}
                            loading={addSupplyMutation.isPending}
                            className="w-full"
                          >
                            Add Material
                          </Button>
                        </>
                      )}
                    </div>

                    {/* Materials List */}
                    {isLoadingSupplies ? (
                      <Spin size="small" />
                    ) : linkedSupplies && linkedSupplies.length > 0 ? (
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {linkedSupplies.map((link) => (
                          <div key={link.id} className="flex items-center justify-between bg-white p-2 rounded border text-xs">
                            <div>
                              <div className="font-medium">{link.supply?.name}</div>
                              <div className="text-gray-500">
                                {CALCULATION_TYPE_OPTIONS.find((c) => c.value === link.calculationType)?.label}
                              </div>
                            </div>
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<X size={14} />}
                              onClick={() => handleRemoveMaterial(link.id)}
                              loading={removeSupplyMutation.isPending}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-gray-400 text-xs text-center py-2">No materials linked</div>
                    )}
                  </div>

                  {/* Cost Breakdown */}
                  <div className="border rounded-lg p-3 bg-green-50">
                    <h4 className="text-sm font-medium mb-2 flex items-center">
                      <Calculator size={16} className="mr-2 text-green-600" />
                      Suggested Charge Calculator
                    </h4>

                    {/* Labor Rate Input */}
                    <div className="mb-2">
                      <label className="text-xs text-gray-500">Labor Rate ($/hr)</label>
                      <InputNumber
                        className="w-full"
                        size="small"
                        value={hourlyRate}
                        onChange={(value) => setHourlyRate(value || 100)}
                        min={0}
                        step={5}
                        prefix="$"
                        addonAfter="/hr"
                      />
                    </div>

                    {isLoadingBreakdown ? (
                      <Spin size="small" />
                    ) : materialBreakdown ? (
                      <div className="text-xs space-y-2">
                        <div className="bg-white rounded p-2 border">
                          <div className="text-gray-500">Surface Area (+5% buffer)</div>
                          <div className="font-bold">{materialBreakdown.dimensions.surfaceAreaSqFt} sq ft</div>
                        </div>

                        {/* Material breakdown by item */}
                        {materialBreakdown.materials && materialBreakdown.materials.length > 0 && (
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {materialBreakdown.materials.map((mat, idx) => (
                              <div key={idx} className="bg-white rounded p-2 border">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <div className="font-medium">{mat.supplyName}</div>
                                    <div className="text-gray-500">
                                      {mat.supplyDimensions && <span>{mat.supplyDimensions} • </span>}
                                      ${mat.pricePerSqFt?.toFixed(2) || '0.00'}/sq ft
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-bold text-green-600">${mat.billableTotal?.toFixed(2) || '0.00'}</div>
                                    <div className="text-gray-500">{mat.quantityNeeded?.toFixed(2)} {mat.unitLabel}</div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <Divider className="my-1" />

                        <div className="flex justify-between">
                          <span>Materials Total:</span>
                          <span className="font-medium">${suggestedCharge.materials.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Labor ({watchedEstimatedMinutes} min @ ${hourlyRate}/hr):</span>
                          <span className="font-medium">${suggestedCharge.labor.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-green-700 bg-green-100 p-2 rounded">
                          <span>Suggested Total:</span>
                          <span>${suggestedCharge.total.toFixed(2)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-400 text-xs text-center py-2">
                        Set dimensions to see calculations
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <Alert
                  message="Materials & Calculations"
                  description="Save the packing type first, then edit it to link materials and see cost calculations."
                  type="info"
                  showIcon
                />
              )}
            </Col>
          </Row>

          <Divider />

          <Form.Item className="mb-0">
            <Space className="w-full justify-end">
              <Button onClick={() => {
                setIsModalVisible(false);
                setEditingMetric(null);
              }}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingMetric ? "Update" : "Create"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
